/**
 * T080: Cluster Storage Configuration Component
 * Configure storage providers and policies for cluster storage
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Card, CardContent, CardHeader, Checkbox, FormControlLabel, Grid, TextField,
	Select, MenuItem, FormControl, InputLabel, FormHelperText, Typography,
	Accordion, AccordionSummary, AccordionDetails, Chip, Box, Divider, Button,
	Tooltip, Paper, IconButton, Collapse, Switch, Icon, Popover,
	Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { 
	ExpandMore as ExpandMoreIcon, 
	Settings as SettingsIcon, 
	Add as AddIcon, 
	Storage as StorageIcon,
	Delete as DeleteIcon,
	Remove as CollapseIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { getStorageProviders, getStoragePolicies } from 'app/store/actions';
import StorageProviderSelector from './StorageProviderSelector';

const useStyles = makeStyles((theme) => ({
	card: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	cardHeader: {
		paddingBottom: '8px',
		backgroundColor: '#f7f9fc'
	},
	section: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	advanced: {
		marginTop: theme.spacing(2)
	},
	infoBox: {
		padding: theme.spacing(2),
		backgroundColor: theme.palette.info.light,
		borderRadius: theme.shape.borderRadius,
		marginTop: theme.spacing(2),
		display: 'flex',
		gap: theme.spacing(1),
		alignItems: 'start'
	},
	policyInfo: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1),
		backgroundColor: theme.palette.grey[100],
		borderRadius: theme.shape.borderRadius,
		fontSize: '0.875rem'
	},
	noProvidersAlert: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
	},
	settingsLink: {
		color: theme.palette.primary.main,
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	storageConfigCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
		backgroundColor: theme.palette.background.default,
		transition: 'all 0.3s ease',
	},
	storageConfigHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: theme.spacing(1, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
	storageConfigContent: {
		padding: theme.spacing(2),
	},
	providerChip: {
		marginLeft: theme.spacing(1)
	},
	fadeInAnimation: {
		animation: '$fadeIn 1.05s ease-in-out'
	},
	'@keyframes fadeIn': {
		'0%': {
			opacity: 0.5
		},
		'100%': {
			opacity: 1
		}
	},
}));

// Helper function to generate random 4-letter string
const generateRandomString = (length = 4) => {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
	let result = '';
	for (let i = 0; i < length; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return result;
};

const ClusterStorageConfig = ({ value, onChange, disabled = false, cloudProvider = 'ibm' }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	
	const providers = useSelector(({ storage }) => storage.providers || []);
	const policies = useSelector(({ storage }) => storage.policies || []);
	const predefinedPolicies = useSelector(({ storage }) => storage.predefinedPolicies || []);
	
	// Support multiple storage configurations (array)
	const [storageConfigurations, setStorageConfigurations] = useState([]);
	const [selectedProvider, setSelectedProvider] = useState('');
	const [expandedConfigs, setExpandedConfigs] = useState({});
	const [newlyAddedConfigs, setNewlyAddedConfigs] = useState(new Set());
	const [backupInfoAnchor, setBackupInfoAnchor] = useState(null);
	const [backupConfirmDialog, setBackupConfirmDialog] = useState({
		open: false,
		configId: null,
		enabled: false,
		frequency: 'daily',
		retention: 7
	});
	
	// Initialize from parent value
	useEffect(() => {
		if (!value) {
			setStorageConfigurations([]);
			return;
		}
		
		// Handle new format with configurations array
		if (value.configurations && Array.isArray(value.configurations)) {
			setStorageConfigurations(value.configurations);
			// Only initialize expansion state if it's empty (first load)
			// This prevents resetting expansion when user is editing fields
			setExpandedConfigs(prev => {
				// If we already have expansion state for these configs, keep it
				const hasExistingState = value.configurations.some(config => config.id in prev);
				if (hasExistingState) {
					return prev; // Keep existing expansion state
				}
				// Otherwise, initialize all as minimized (default on page load)
				const expanded = {};
				value.configurations.forEach(config => {
					expanded[config.id] = false;
				});
				return expanded;
			});
		}
		// Handle old format (single config) - convert to new format
		else if (value.enabled && value.provider) {
			// Find provider details
			const provider = providers.find(p => p._id === value.provider);
			const configId = `config-${Date.now()}`;
			const convertedConfig = {
				id: configId,
				provider: value.provider,
				providerName: provider?.name || 'Storage Provider',
				providerType: provider?.type || 'heidi-ai',
				name: `Storage-${generateRandomString()}`,
				headNodeVolumes: value.headNodeVolumes || [],
				computeNodeIps: value.computeNodeIps || []
			};
			setStorageConfigurations([convertedConfig]);
			setExpandedConfigs({ [configId]: false });
		}
		// No storage configured
		else {
			setStorageConfigurations([]);
		}
	}, [value, providers]);
	
	// Load providers and policies on mount
	useEffect(() => {
		dispatch(getStorageProviders());
	}, [dispatch]);
	
	// Notify parent of changes
	const notifyChange = (configs) => {
		const newValue = {
			enabled: configs.length > 0,
			configurations: configs
		};
		if (onChange) {
			onChange(newValue);
		}
	};
	
	// Toggle accordion expansion
	const toggleConfigExpansion = (configId) => {
		setExpandedConfigs(prev => ({
			...prev,
			[configId]: !prev[configId]
		}));
	};
	
	// Add new storage configuration
	const addStorageConfiguration = () => {
		if (!selectedProvider) return;
		
		const provider = providers.find(p => p._id === selectedProvider);
		if (!provider) return;
		
		const configId = `config-${Date.now()}`;
		const newConfig = {
			id: configId,
			provider: selectedProvider,
			providerName: provider.name,
			providerType: provider.type,
			name: `Storage-${generateRandomString()}`,
			headNodeVolumes: [{
				policy: '',
				mountPath: '/data',
				backupEnabled: false,
				backupFrequency: 'daily',
				backupRetentionDays: 7,
				overrides: {
					capacity: null,
					iops: null,
					throughput: null
				}
			}],
			computeNodeIps: []
		};
		
		const updatedConfigs = [...storageConfigurations, newConfig];
		setStorageConfigurations(updatedConfigs);
		
		// Close ALL existing configs and open ONLY the new one
		const newExpandedState = {};
		updatedConfigs.forEach(config => {
			newExpandedState[config.id] = config.id === configId; // true only for new config
		});
		setExpandedConfigs(newExpandedState);
		
		// Mark as newly added for animation
		setNewlyAddedConfigs(prev => new Set(prev).add(configId));
		setTimeout(() => {
			setNewlyAddedConfigs(prev => {
				const updated = new Set(prev);
				updated.delete(configId);
				return updated;
			});
		}, 1050);
		
		// Reset provider selection
		setSelectedProvider('');
		
		// Load policies for this provider
		dispatch(getStoragePolicies(selectedProvider));
		
		notifyChange(updatedConfigs);
	};
	
	// Remove storage configuration
	const removeStorageConfiguration = (configId) => {
		const updatedConfigs = storageConfigurations.filter(c => c.id !== configId);
		setStorageConfigurations(updatedConfigs);
		notifyChange(updatedConfigs);
	};
	
	// Update storage configuration field
	const updateStorageConfiguration = (configId, field, value) => {
		// Special handling for backup settings - they apply cluster-wide
		if (field === 'headVolume.backupEnabled' && value === true) {
			// Check if there are multiple Heidi AI configurations
			const heidiConfigs = storageConfigurations.filter(c => c.providerType === 'heidi-ai');
			
			if (heidiConfigs.length > 1) {
				// Show confirmation dialog
				const currentConfig = storageConfigurations.find(c => c.id === configId);
				const currentVolume = currentConfig?.headNodeVolumes?.[0] || {};
				
				setBackupConfirmDialog({
					open: true,
					configId: configId,
					enabled: true,
					frequency: currentVolume.backupFrequency || 'daily',
					retention: currentVolume.backupRetentionDays || 7
				});
				return;
			}
		}
		
		// Regular update logic
		const updatedConfigs = storageConfigurations.map(config => {
			// Determine if this is a backup-related field
			const isBackupField = field === 'headVolume.backupEnabled' || 
			                       field === 'headVolume.backupFrequency' || 
			                       field === 'headVolume.backupRetentionDays';
			
			// For backup fields, update ALL Heidi AI configs (cluster-wide setting)
			// For other fields, only update the specific config
			const shouldUpdate = config.id === configId || (isBackupField && config.providerType === 'heidi-ai');
			
			if (shouldUpdate) {
				// Check for more specific case first (headVolume.overrides.X)
				if (field.startsWith('headVolume.overrides.')) {
					const overrideField = field.split('.')[2];
					return {
						...config,
						headNodeVolumes: [{
							...config.headNodeVolumes[0],
							overrides: {
								...config.headNodeVolumes[0].overrides,
								[overrideField]: value ? parseInt(value, 10) : null
							}
						}]
					};
				} else if (field.startsWith('headVolume.')) {
					const volumeField = field.split('.')[1];
					return {
						...config,
						headNodeVolumes: [{
							...config.headNodeVolumes[0],
							[volumeField]: value
						}]
					};
				} else {
					return {
						...config,
						[field]: value
					};
				}
			}
			return config;
		});
		setStorageConfigurations(updatedConfigs);
		notifyChange(updatedConfigs);
	};
	
	// Handle backup confirmation dialog
	const handleBackupConfirmation = (confirmed) => {
		if (confirmed) {
			// Apply backup settings to ALL Heidi AI configurations
			const updatedConfigs = storageConfigurations.map(config => {
				if (config.providerType === 'heidi-ai') {
					return {
						...config,
						headNodeVolumes: [{
							...config.headNodeVolumes[0],
							backupEnabled: backupConfirmDialog.enabled,
							backupFrequency: backupConfirmDialog.frequency,
							backupRetentionDays: backupConfirmDialog.retention
						}]
					};
				}
				return config;
			});
			
			setStorageConfigurations(updatedConfigs);
			notifyChange(updatedConfigs);
		}
		
		// Close dialog
		setBackupConfirmDialog({
			open: false,
			configId: null,
			enabled: false,
			frequency: 'daily',
			retention: 7
		});
	};
	
	// Load policies when provider changes
	useEffect(() => {
		storageConfigurations.forEach(config => {
			if (config.provider) {
				dispatch(getStoragePolicies(config.provider));
			}
		});
	}, [dispatch, storageConfigurations]);
	
	// Get available policies for a specific provider
	const getAvailablePolicies = (providerId) => {
		if (!providerId) return [];
		return (policies || []).filter((p) => {
			const pid = (p.provider && typeof p.provider === 'object') ? p.provider._id : p.provider;
			return pid === providerId;
		});
	};
	
	return (
		<Card className={classes.card}>
			<CardHeader
				className={classes.cardHeader}
				avatar={<StorageIcon />}
				title={
					<Box display="flex" alignItems="center">
						<Typography style={{ fontSize: '16px', fontWeight: 600, color: '#0f2147' }}>
							High-Performance Data Infrastructure
						</Typography>
					</Box>
				}
			/>
			<CardContent>
				{/* Add New Data Infrastructure */}
				<Box display="flex" alignItems="center" gap={2} marginBottom={2}>
					<Box style={{ minWidth: 200, marginRight: 20 }}>
						<StorageProviderSelector
							value={selectedProvider}
							onChange={(providerId) => setSelectedProvider(providerId)}
							cloudProvider={cloudProvider}
							disabled={disabled}
							required={false}
							helperText=""
							label="Select Provider"
						/>
					</Box>
					<Tooltip 
						title={selectedProvider ? "Add storage configuration" : "Select a provider first"}
						placement="top"
					>
						<span>
							<Button
								variant="contained"
								color="primary"
								startIcon={<AddIcon />}
								onClick={addStorageConfiguration}
								disabled={!selectedProvider || disabled}
							>
								Add
							</Button>
						</span>
					</Tooltip>
				</Box>

				{/* Existing storage configurations */}
				{storageConfigurations.map((config, index) => {
					const configId = config.id || `fallback-${index}-${Date.now()}`;
					const isExpanded = expandedConfigs[configId] === true;
					const isNewlyAdded = newlyAddedConfigs.has(configId);
					const headVolume = config.headNodeVolumes && config.headNodeVolumes[0];
					const availablePolicies = getAvailablePolicies(config.provider);
					const selectedPolicy = availablePolicies.find(p => p._id === headVolume?.policy);
					
					return (
						<Paper 
							key={`storage-config-${configId}`} 
							className={`${classes.storageConfigCard} ${isNewlyAdded ? classes.fadeInAnimation : ''}`}
							elevation={1}
						>
							{/* Provider Header */}
							<Box 
								className={classes.storageConfigHeader}
								onClick={() => toggleConfigExpansion(configId)}
							>
								<Box display="flex" alignItems="center">
									<StorageIcon style={{ marginRight: 8 }} />
									<Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
										{config.name || config.providerName}
									</Typography>
									<Chip 
										label={config.providerType.toUpperCase()} 
										size="small" 
										className={classes.providerChip}
										variant="outlined"
										color="primary"
									/>
									{!isExpanded && headVolume && (
										<Box display="flex" alignItems="center" marginLeft={2}>
											<Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
												•
											</Typography>
											<Typography variant="body2" color="textSecondary">
												{headVolume.mountPath || '/data'}
											</Typography>
											{selectedPolicy && (
												<Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
													• Policy: {selectedPolicy.name}
												</Typography>
											)}
										</Box>
									)}
								</Box>
								<Box display="flex" alignItems="center">
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											toggleConfigExpansion(configId);
										}}
										disabled={disabled}
										title={isExpanded ? "Minimize" : "Expand"}
									>
										{isExpanded ? <CollapseIcon /> : <ExpandMoreIcon />}
									</IconButton>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											removeStorageConfiguration(configId);
										}}
										disabled={disabled}
										title="Delete"
									>
										<DeleteIcon />
									</IconButton>
								</Box>
							</Box>

							{/* Configuration Content - Collapsible */}
							<Collapse in={isExpanded}>
								<Box className={classes.storageConfigContent}>
									<Grid container spacing={3}>
										{/* Configuration Name */}
										<Grid item xs={12}>
											<TextField
												fullWidth
												label="Configuration Name"
												value={config.name || ''}
												onChange={(e) => updateStorageConfiguration(configId, 'name', e.target.value)}
												disabled={disabled}
												size="small"
												required
											/>
										</Grid>
										
										{/* Head Node Volume Configuration */}
										<Grid item xs={12}>
											<Typography variant="subtitle2" gutterBottom color="primary">
												<SettingsIcon style={{ verticalAlign: 'middle', marginRight: 4 }} />
												Head Node Volume Configuration
											</Typography>
										</Grid>
										
										<Grid item xs={12} md={6}>
											<FormControl fullWidth required>
												<InputLabel>Storage Policy</InputLabel>
												<Select
													value={headVolume?.policy || ''}
													onChange={(e) => updateStorageConfiguration(configId, 'headVolume.policy', e.target.value)}
													disabled={disabled}
												>
													<MenuItem value="">
														<em>Select a policy</em>
													</MenuItem>
													{availablePolicies.map((policy) => (
														<MenuItem key={policy._id} value={policy._id}>
															{policy.name}
															{policy.isPredefined && ' (Predefined)'}
														</MenuItem>
													))}
												</Select>
												<FormHelperText>
													Choose a predefined or custom storage policy
												</FormHelperText>
											</FormControl>
											
											{selectedPolicy && (
												<Box className={classes.policyInfo} style={{ marginTop: 24 }}>
													<Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
														Policy Details:
													</Typography>
													{/* IBM details */}
													{selectedPolicy.ibmParams && (
														<Box display="flex" flexDirection="column" gap={2}>
															<TextField
																label="Capacity"
																value={`${selectedPolicy.ibmParams.capacity} GB`}
																disabled
																fullWidth
																size="small"
																InputProps={{
																	style: { color: '#000000' }
																}}
																InputLabelProps={{
																	shrink: true
																}}
															/>
															<TextField
																label="IOPS"
																value={selectedPolicy.ibmParams.iops.toLocaleString()}
																disabled
																fullWidth
																size="small"
																InputProps={{
																	style: { color: '#000000' }
																}}
																InputLabelProps={{
																	shrink: true
																}}
															/>
															<TextField
																label="Throughput"
																value={`${selectedPolicy.ibmParams.throughput} MB/s`}
																disabled
																fullWidth
																size="small"
																InputProps={{
																	style: { color: '#000000' }
																}}
																InputLabelProps={{
																	shrink: true
																}}
															/>
														</Box>
													)}
													{/* Volumez details */}
													{selectedPolicy.volumezParams && (
														<>
															<Typography variant="body2">
																Optimization: {selectedPolicy.volumezParams.capacityoptimization === 'capacity' ? 'Capacity' : 'Performance'}
															</Typography>
															<Typography variant="body2">
																Read IOPS: {selectedPolicy.volumezParams.iopsread?.toLocaleString?.() || selectedPolicy.volumezParams.iopsread}
															</Typography>
															<Typography variant="body2">
																Write IOPS: {selectedPolicy.volumezParams.iopswrite?.toLocaleString?.() || selectedPolicy.volumezParams.iopswrite}
															</Typography>
															<Typography variant="body2">
																Read BW: {selectedPolicy.volumezParams.bandwidthread} MB/s, Write BW: {selectedPolicy.volumezParams.bandwidthwrite} MB/s
															</Typography>
														</>
													)}
												</Box>
											)}
										</Grid>
										
										<Grid item xs={12} md={6}>
											<TextField
												label="Mount Path"
												value={headVolume?.mountPath || ''}
												onChange={(e) => updateStorageConfiguration(configId, 'headVolume.mountPath', e.target.value)}
												disabled={disabled}
												required
												fullWidth
												helperText="Directory path where storage will be mounted (e.g., /data)"
											/>
											
											{/* Backup Configuration Section */}
											<Box mt={3}>
												<Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
													Backup Configuration:
												</Typography>
												
												{/* Cluster-wide backup notice */}
												{storageConfigurations.filter(c => c.providerType === 'heidi-ai').length > 1 && (
													<Alert severity="info" style={{ marginBottom: 12, fontSize: '0.875rem' }}>
														<Typography variant="caption" display="block">
															<strong>Cluster-Wide Setting:</strong> Backup configuration applies to ALL volumes in ALL Heidi AI configurations.
														</Typography>
													</Alert>
												)}
												
												{/* Backup Enable/Disable Switch */}
												<Box display="flex" alignItems="center" mb={2}>
													<FormControlLabel
														control={
															<Switch
																checked={headVolume?.backupEnabled || false}
																onChange={(e) => updateStorageConfiguration(configId, 'headVolume.backupEnabled', e.target.checked)}
																color="primary"
																disabled={disabled}
															/>
														}
														label={
															<Typography variant="body2" style={{ fontWeight: 500 }}>
																Enable Backups
															</Typography>
														}
													/>
													<IconButton
														size="small"
														onClick={(e) => setBackupInfoAnchor(e.currentTarget)}
														style={{ marginLeft: 4, padding: 4 }}
													>
														<Icon style={{ fontSize: 18, color: '#1976d2' }}>info</Icon>
													</IconButton>
													<Popover
														open={Boolean(backupInfoAnchor)}
														anchorEl={backupInfoAnchor}
														onClose={() => setBackupInfoAnchor(null)}
														anchorOrigin={{
															vertical: 'bottom',
															horizontal: 'center',
														}}
														transformOrigin={{
															vertical: 'top',
															horizontal: 'center',
														}}
													>
														<Box p={2} maxWidth={320}>
															<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
																About Automated Backups
															</Typography>
															<Typography variant="body2" color="textSecondary" paragraph>
																IBM Cloud Block Storage snapshots are created automatically based on your schedule.
															</Typography>
															<Typography variant="body2" color="textSecondary">
																Backups are incremental and tagged with the cluster name for easy identification.
															</Typography>
														</Box>
													</Popover>
												</Box>
												
												{/* Backup Configuration Fields - Show when backups enabled */}
												{headVolume?.backupEnabled && (
													<Box display="flex" flexDirection="column" gap={2}>
														<FormControl fullWidth size="small">
															<InputLabel>Backup Frequency</InputLabel>
															<Select
																value={headVolume?.backupFrequency || 'daily'}
																onChange={(e) => updateStorageConfiguration(configId, 'headVolume.backupFrequency', e.target.value)}
																disabled={disabled}
															>
																<MenuItem value="hourly">Hourly</MenuItem>
																<MenuItem value="daily">Daily (2:00 AM)</MenuItem>
																<MenuItem value="weekly">Weekly (Sunday 2:00 AM)</MenuItem>
															</Select>
															<FormHelperText>How often to create backup snapshots</FormHelperText>
														</FormControl>
														
														<TextField
															label="Retention Days"
															type="number"
															value={headVolume?.backupRetentionDays || 7}
															onChange={(e) => updateStorageConfiguration(configId, 'headVolume.backupRetentionDays', e.target.value)}
															disabled={disabled}
															size="small"
															fullWidth
															inputProps={{ min: 1, max: 90 }}
															helperText="Number of days to keep backups (1-90)"
														/>
													</Box>
												)}
											</Box>
										</Grid>
										
										{/* Advanced: Custom Overrides */}
										<Grid item xs={12}>
											<Accordion className={classes.advanced}>
												<AccordionSummary expandIcon={<ExpandMoreIcon />}>
													<Typography>Advanced: Custom Performance Overrides</Typography>
												</AccordionSummary>
												<AccordionDetails>
													<Grid container spacing={2}>
														<Grid item xs={12}>
															<Typography variant="body2" color="textSecondary" gutterBottom>
																Override policy defaults with custom values (optional). Leave blank to use policy defaults.
															</Typography>
														</Grid>
														<Grid item xs={12} md={4}>
															<TextField
																type="number"
																label="Capacity Override (GB)"
																value={headVolume?.overrides?.capacity || ''}
																onChange={(e) => updateStorageConfiguration(configId, 'headVolume.overrides.capacity', e.target.value)}
																disabled={disabled}
																inputProps={{ min: 10, max: 16000 }}
																fullWidth
																helperText="10-16,000 GB"
															/>
														</Grid>
														<Grid item xs={12} md={4}>
															<TextField
																type="number"
																label="IOPS Override"
																value={headVolume?.overrides?.iops || ''}
																onChange={(e) => updateStorageConfiguration(configId, 'headVolume.overrides.iops', e.target.value)}
																disabled={disabled}
																inputProps={{ min: 100, max: 64000 }}
																fullWidth
																helperText="100-64,000"
															/>
														</Grid>
														<Grid item xs={12} md={4}>
															<TextField
																type="number"
																label="Throughput Override (MB/s)"
																value={headVolume?.overrides?.throughput || ''}
																onChange={(e) => updateStorageConfiguration(configId, 'headVolume.overrides.throughput', e.target.value)}
																disabled={disabled}
																inputProps={{ min: 1, max: 1024 }}
																fullWidth
																helperText="1-1,024 MB/s"
															/>
														</Grid>
													</Grid>
												</AccordionDetails>
											</Accordion>
										</Grid>
									</Grid>
								</Box>
							</Collapse>
						</Paper>
					);
				})}

				{/* No configurations message */}
				{storageConfigurations.length === 0 && (
					<Box 
						display="flex" 
						flexDirection="column" 
						alignItems="center" 
						justifyContent="center"
						padding={0}
						textAlign="center"
					>
						<Typography variant="body1" color="textSecondary" gutterBottom>
							No high-performance storage configured
						</Typography>
						<Typography variant="caption" color="textSecondary">
							Select a provider from the dropdown above and click "Add" to configure high-performance data infrastructure for your cluster.
						</Typography>
					</Box>
				)}
			</CardContent>
			
			{/* Backup Confirmation Dialog */}
			<Dialog
				open={backupConfirmDialog.open}
				onClose={() => handleBackupConfirmation(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<Box display="flex" alignItems="center" gap={1}>
						<Icon color="primary">info</Icon>
						<Typography variant="h6">Enable Cluster-Wide Backups</Typography>
					</Box>
				</DialogTitle>
				<DialogContent>
					<DialogContentText component="div">
						<Typography variant="body1" gutterBottom>
							<strong>Important:</strong> Backup settings apply to <strong>ALL volumes</strong> in <strong>ALL Heidi AI configurations</strong> for this cluster.
						</Typography>
						<Typography variant="body2" color="textSecondary" paragraph>
							IBM Cloud uses a single backup policy per cluster that matches all volumes by cluster tag.
						</Typography>
						<Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
							<Typography variant="subtitle2" gutterBottom>
								This will enable backups for:
							</Typography>
							<ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
								{storageConfigurations
									.filter(c => c.providerType === 'heidi-ai')
									.map((config, idx) => (
										<li key={config.id}>
											<Typography variant="body2">
												<strong>{config.name || `Configuration ${idx + 1}`}</strong> - All volumes
											</Typography>
										</li>
									))}
							</ul>
						</Box>
						<Box mt={2}>
							<Typography variant="body2" color="textSecondary">
								<strong>Schedule:</strong> {
									backupConfirmDialog.frequency === 'hourly' ? 'Hourly' :
									backupConfirmDialog.frequency === 'daily' ? 'Daily at 2:00 AM' :
									'Weekly (Sunday at 2:00 AM)'
								}
							</Typography>
							<Typography variant="body2" color="textSecondary">
								<strong>Retention:</strong> {backupConfirmDialog.retention} days
							</Typography>
						</Box>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleBackupConfirmation(false)} color="default">
						Cancel
					</Button>
					<Button onClick={() => handleBackupConfirmation(true)} color="primary" variant="contained">
						Enable Backups for All Volumes
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};

export default ClusterStorageConfig;
